import React, { useState, useEffect } from 'react';
import jsonData from '../../Components/dummydataRH/cleaned_data.json';
import { 
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    tableCellClasses,
    CircularProgress,
    Chip,
    Tooltip,
    useTheme,
    Box,
    styled,
    Avatar,
    Fade,
    IconButton,
    Badge
} from '@mui/material';
import { getFirestore, getDocs, collection } from 'firebase/firestore';
import {
    AttachMoney,
    Work,
    Person,
    MonetizationOn,
    AccountBalance,
    Warning,
    Info,
    GroupWork,
    Refresh,
    TrendingUp,
    Receipt,
    Calculate,
    Close
} from '@mui/icons-material';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 600,
        fontSize: 11,
        padding: theme.spacing(0.75, 0.5),
        whiteSpace: 'nowrap',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 11,
        padding: theme.spacing(0.5, 0.5),
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
        transition: 'background-color 0.2s ease',
    },
}));

const StatCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.main}25 100%)`,
    border: `1px solid ${theme.palette.primary.light}30`,
}));

const ExcelSearchScreen = ({ selectedWorkerId, onClose }) => {
    const theme = useTheme();
    const db = getFirestore();
    const [filteredRows, setFilteredRows] = useState([]);
    const [workers, setWorkers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const [isWindowVisible, setIsWindowVisible] = useState(true);
    const [stats, setStats] = useState({ totalCost: 0, employeeCount: 0 });
    const restaurantId = localStorage.getItem("restaurantId");

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsWindowVisible(!document.hidden);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            if (isWindowVisible) {
                getAllWorkersByRestaurantId(restaurantId);
                setLastRefresh(new Date());
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [isWindowVisible, restaurantId]);

    const getAllWorkersByRestaurantId = async (restaurantId) => {
        try {
            const workersSnapshot = await getDocs(collection(db, 'workers'));
            let workersData = workersSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            if (selectedWorkerId) {
                workersData = workersData.filter((worker) => worker.id === selectedWorkerId);
            }

            setWorkers(workersData);
        } catch (error) {
            console.error('Error fetching worker data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getAllWorkersByRestaurantId(restaurantId);
    }, [selectedWorkerId, restaurantId]);

    const filterRowsForWorker = (worker) => {
        return jsonData.filter((row) => {
            const rowNetValue = Math.floor(Number(row.net));
            const inputNetValue = Math.floor(Number(worker.salary));
            const isNetMatch = rowNetValue === inputNetValue;
            const isSituationMatch = row.Situation === worker.statue;
            return isNetMatch && isSituationMatch;
        });
    };

    useEffect(() => {
        if (workers.length > 0) {
            const updatedFilteredRows = workers
                .filter((worker) => !selectedWorkerId || worker.id === selectedWorkerId || worker.restaurantId === restaurantId)
                .map((worker) => ({
                    worker,
                    rows: filterRowsForWorker(worker),
                }));
            setFilteredRows(updatedFilteredRows);
            
            let total = 0;
            let count = 0;
            updatedFilteredRows.forEach(workerData => {
                if (workerData.worker.restaurantId === restaurantId && workerData.rows.length > 0) {
                    count++;
                    workerData.rows.forEach(row => {
                        total += row['Salaire brut'] * 0.1657 +
                                row['Salaire brut'] * 0.01 +
                                row['Salaire brut'] * 0.02 +
                                row['Salaire brut'] * 0.005 +
                                row['Salaire brut'];
                    });
                }
            });
            setStats({ totalCost: total, employeeCount: count });
        }
    }, [workers, selectedWorkerId, restaurantId]);

    const formatNumber = (value) => 
        new Intl.NumberFormat('fr-TN', { 
            minimumFractionDigits: 3,
            maximumFractionDigits: 3 
        }).format(value);

    const formatCurrency = (value) => 
        new Intl.NumberFormat('fr-TN', { 
            style: 'currency',
            currency: 'TND',
            minimumFractionDigits: 3,
            maximumFractionDigits: 3
        }).format(value);

    // Table headers with abbreviations for smaller display
    const headers = [
        { label: 'Employé', icon: Person, tooltip: 'Employé' },
        { label: 'Sit.', icon: null, tooltip: 'Situation' },
        { label: 'Brut', icon: AttachMoney, tooltip: 'Salaire brut' },
        { label: 'CNSS', icon: null, tooltip: 'CNSS' },
        { label: 'Imp.', icon: null, tooltip: 'Salaire Imposable' },
        { label: 'IRPP', icon: null, tooltip: 'IRPP' },
        { label: 'CSS', icon: null, tooltip: 'CSS' },
        { label: 'Net', icon: null, tooltip: 'Net à payer' },
        { label: 'TFP', icon: null, tooltip: 'TFP (2%)' },
        { label: 'FOPR', icon: null, tooltip: 'FOPROLOS (1%)' },
        { label: 'Sécu.', icon: null, tooltip: 'Sécurité Sociale (16.57%)' },
        { label: 'Acc.', icon: null, tooltip: 'Accident (0.5%)' },
        { label: 'Total', icon: TrendingUp, tooltip: 'Coût Total' }
    ];

    return (
        <Container maxWidth="xl" sx={{ py: 1, px: 1 }}>
            {/* Compact Header */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 1,
                gap: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ 
                        bgcolor: theme.palette.primary.main,
                        width: 32,
                        height: 32
                    }}>
                        <Calculate sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                            Cotisations
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                            Salaires & charges
                        </Typography>
                    </Box>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {/* Compact Stats */}
                    <Fade in={!isLoading}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <StatCard elevation={0} sx={{ px: 1 }}>
                                <GroupWork sx={{ color: theme.palette.primary.main, fontSize: 14 }} />
                                <Typography variant="caption" fontWeight={600}>
                                    {stats.employeeCount}
                                </Typography>
                            </StatCard>
                            
                            <StatCard elevation={0} sx={{ px: 1 }}>
                                <AttachMoney sx={{ color: theme.palette.success.main, fontSize: 14 }} />
                                <Typography variant="caption" fontWeight={600} color="success.main">
                                    {formatCurrency(stats.totalCost).replace('TND', '')}
                                </Typography>
                            </StatCard>
                        </Box>
                    </Fade>

                    <Tooltip title={`Dernière mise à jour: ${lastRefresh.toLocaleTimeString()}`} arrow>
                        <Refresh sx={{ fontSize: 14, color: theme.palette.text.secondary, cursor: 'pointer' }} 
                            onClick={() => getAllWorkersByRestaurantId(restaurantId)} />
                    </Tooltip>

                    {onClose && (
                        <IconButton size="small" onClick={onClose} sx={{ p: 0.5 }}>
                            <Close sx={{ fontSize: 16 }} />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Main Table */}
            <Fade in={!isLoading}>
                <Paper elevation={1} sx={{ borderRadius: 1, overflow: 'hidden' }}>
                    {isLoading ? (
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={24} thickness={4} />
                        </Box>
                    ) : filteredRows.length > 0 ? (
                        <TableContainer sx={{ maxHeight: 'calc(100vh - 100px)' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {headers.map((header, idx) => (
                                            <StyledTableCell key={idx}>
                                                <Tooltip title={header.tooltip} arrow placement="top">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        {header.icon && <header.icon sx={{ fontSize: 12 }} />}
                                                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 10 }}>
                                                            {header.label}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            </StyledTableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRows
                                        .filter(workerData => workerData.worker.restaurantId === restaurantId)
                                        .map((workerData, index) => (
                                            <React.Fragment key={index}>
                                                {workerData.rows.length > 0 ? (
                                                    workerData.rows.map((row, rowIndex) => (
                                                        <StyledTableRow key={rowIndex}>
                                                            <StyledTableCell>
                                                                <Chip
                                                                    label={workerData.worker.name}
                                                                    size="small"
                                                                    sx={{ 
                                                                        fontWeight: 500,
                                                                        height: 20,
                                                                        fontSize: 10,
                                                                        '& .MuiChip-label': { px: 0.5 }
                                                                    }}
                                                                />
                                                            </StyledTableCell>
                                                            <StyledTableCell>
                                                                <Chip
                                                                    label={row.Situation === 'Marie' ? 'M' : 'C'}
                                                                    size="small"
                                                                    color={row.Situation === 'Marie' ? 'success' : 'default'}
                                                                    sx={{ 
                                                                        minWidth: 28,
                                                                        height: 20,
                                                                        fontSize: 10,
                                                                        '& .MuiChip-label': { px: 0.5 }
                                                                    }}
                                                                />
                                                            </StyledTableCell>
                                                            {[
                                                                'Salaire brut',
                                                                'cnss',
                                                                'Salaire Imposable',
                                                                'IRPP',
                                                                'CSS',
                                                                'net'
                                                            ].map((key) => (
                                                                <StyledTableCell key={key}>
                                                                    <Typography variant="caption" sx={{ fontSize: 10 }}>
                                                                        {formatNumber(row[key])}
                                                                    </Typography>
                                                                </StyledTableCell>
                                                            ))}
                                                            <StyledTableCell>
                                                                <Typography variant="caption" sx={{ fontSize: 10 }}>
                                                                    {formatNumber(row['Salaire brut'] * 0.02)}
                                                                </Typography>
                                                            </StyledTableCell>
                                                            <StyledTableCell>
                                                                <Typography variant="caption" sx={{ fontSize: 10 }}>
                                                                    {formatNumber(row['Salaire brut'] * 0.01)}
                                                                </Typography>
                                                            </StyledTableCell>
                                                            <StyledTableCell>
                                                                <Typography variant="caption" sx={{ fontSize: 10 }}>
                                                                    {formatNumber(row['Salaire brut'] * 0.1657)}
                                                                </Typography>
                                                            </StyledTableCell>
                                                            <StyledTableCell>
                                                                <Typography variant="caption" sx={{ fontSize: 10 }}>
                                                                    {formatNumber(row['Salaire brut'] * 0.005)}
                                                                </Typography>
                                                            </StyledTableCell>
                                                            <StyledTableCell sx={{ 
                                                                fontWeight: 600,
                                                                color: theme.palette.success.dark,
                                                                bgcolor: theme.palette.success.light + '10'
                                                            }}>
                                                                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 10 }}>
                                                                    {formatCurrency(
                                                                        row['Salaire brut'] * 0.1657 +
                                                                        row['Salaire brut'] * 0.01 +
                                                                        row['Salaire brut'] * 0.02 +
                                                                        row['Salaire brut'] * 0.005 +
                                                                        row['Salaire brut']
                                                                    )}
                                                                </Typography>
                                                            </StyledTableCell>
                                                        </StyledTableRow>
                                                    ))
                                                ) : (
                                                    <StyledTableRow>
                                                        <StyledTableCell colSpan={13}>
                                                            <Box sx={{ 
                                                                py: 1,
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                justifyContent: 'center',
                                                                gap: 0.5,
                                                                color: theme.palette.text.secondary
                                                            }}>
                                                                <Warning sx={{ fontSize: 14 }} />
                                                                <Typography variant="caption">
                                                                    Aucune donnée
                                                                </Typography>
                                                            </Box>
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                )}
                                            </React.Fragment>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box sx={{ 
                            py: 2,
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center', 
                            justifyContent: 'center',
                            gap: 0.5,
                            color: theme.palette.text.secondary
                        }}>
                            <Receipt sx={{ fontSize: 24, opacity: 0.5 }} />
                            <Typography variant="caption">
                                Aucun employé trouvé
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Fade>
        </Container>
    );
};

export default ExcelSearchScreen;